import React, { useMemo, useState } from 'react';
import { components, InputActionMeta, SingleValueProps } from 'react-select';
import { useLazyQuery } from '@apollo/client';
import { colors } from '../../../../config/styles';
import {
  AnalysisStatus,
  IAnalysisFilter,
  ISearchCohortAnalysesQuery,
  ISearchCohortAnalysesQueryVariables,
  ISearchCohortEntityFieldsFragment,
  QueryType,
} from '../../../../utils/apollo/graphqlSchema';
import { ReactSelectMenu } from '../../../Dropdown/StyledReactSelect';
import { useDebounce } from '../../../../utils/hooks';
import { SEARCH_COHORT_ANALYSES } from './queries';
import BuilderReportMenuInfo from './BuilderReportMenuInfo';

interface IProps {
  activeSchemaId: string;
  analysisId: string | null;
  queryType: QueryType.Expression | QueryType.Funnel | QueryType.Retention;
  onChange: (selectedReport: ISearchCohortEntityFieldsFragment | null) => void;
  placeholder?: string;
  hasError?: boolean;
  analysisFilter?: IAnalysisFilter;
}

const BuilderReportMenuLabel = (
  props: SingleValueProps<ISearchCohortEntityFieldsFragment, false>,
) => {
  const { data, children, clearValue } = props;

  if (!props?.data?.id) {
    return (
      <components.SingleValue {...props}>{children}</components.SingleValue>
    );
  }

  return (
    <components.SingleValue {...props}>
      <BuilderReportMenuInfo analysisId={data.id} onClear={clearValue} />
    </components.SingleValue>
  );
};

const BuilderReportMenu = ({
  activeSchemaId,
  analysisId,
  queryType,
  onChange,
  placeholder,
  hasError,
  analysisFilter,
}: IProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchQuery = useDebounce(searchTerm, 300);

  const [loadAnalyses, { data, loading, called }] = useLazyQuery<
    ISearchCohortAnalysesQuery,
    ISearchCohortAnalysesQueryVariables
  >(SEARCH_COHORT_ANALYSES, {
    variables: {
      userOnly: false,
      schemaIds: [activeSchemaId],
      term: debouncedSearchQuery,
      kind: null,
      queryTypes: queryType,
      pagination: { after: 0, first: 25 },
      includes: [],
      // We need a list of reports with status SUCCESS only
      statuses: [AnalysisStatus.Success],
      filter: analysisFilter || null,
    },
  });

  const handleLoadOptions = () => {
    // Initial load only when menu is first opened
    if (!called) {
      loadAnalyses();
    }
  };

  // This callback would be fired even when input hasn't changed
  const handleSearch = (term: string, actionMeta: InputActionMeta) => {
    // Using actionMeta to detect cases to avoid refetch when:
    // 1. Input hasn't been changed and user opens-closes menu;
    // 2. Input hasn't been changed and user selects an option;
    if (actionMeta.action === 'menu-close' && !actionMeta.prevInputValue) {
      return;
    }

    setSearchTerm(term);
  };

  const selectedOption: any = useMemo(() => {
    if (analysisId) {
      return {
        __typename: undefined,
        id: analysisId,
        display: '',
        start: '',
        end: '',
        query: {
          __typename: undefined,
          subject: null,
        },
      };
    }

    return null;
  }, [analysisId]);

  return (
    <ReactSelectMenu
      isMulti={false}
      options={data?.searchAnalysis?.entities ?? []}
      getOptionLabel={(option) => option.display}
      getOptionValue={(option) => option.id}
      value={selectedOption}
      onMenuOpen={handleLoadOptions}
      onChange={onChange}
      onInputChange={handleSearch}
      components={{ SingleValue: BuilderReportMenuLabel }}
      hideSelectedOptions
      isLoading={loading}
      placeholder={placeholder}
      styles={{
        control: (base) => {
          const result = {
            ...base,
            maxWidth: '480px',
          };

          if (hasError) {
            return {
              ...result,
              borderColor: colors.mainError,
            };
          }

          return result;
        },
      }}
    />
  );
};

export default BuilderReportMenu;
